// src/pages/LeavePolicies.jsx
// Settings → Leave policies. How annual leave works for each group of people:
// fixed or accruing, days or hours, the company's leave year or a 12-month
// cycle from each person's start date, part-time pro-rating, carry-over and
// whether public holidays come off the balance.
//
// Templates give sensible local defaults for the UK, Australia and South
// Africa; every value stays editable so an employer can apply its own lawful
// contractual terms. Statutory minimums only ever produce a warning.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layers, Plus, MoreHorizontal, Star, Users, Trash2, Edit2, Upload, Building2, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import {
  cx, fullName, unwrapList, errorMessage,
  Button, IconButton, Chip, EmptyState, SkeletonRows, Notice, SectionHeader, Toggle, Segmented, SearchInput,
  Menu, Modal, ModalHeader, ModalBody, ModalFooter, ModalIcon, useConfirm,
  inputClass, selectClass, textareaClass, labelClass,
} from './settingsUI';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const FREQUENCY_LABEL = {
  daily: 'continuously',
  weekly: 'weekly',
  fortnightly: 'fortnightly',
  monthly: 'monthly',
};

const BLANK_POLICY = {
  name: '',
  description: '',
  allowanceSource: 'policy',
  unit: 'days',
  entitlementMethod: 'fixed',
  annualEntitlement: 25,
  accrualBasis: 'calendar',
  accrualFrequency: 'daily',
  accrualRatePerHour: '',
  pauseAccrualOnUnpaidLeave: false,
  fullTimeDaysPerWeek: 5,
  fullTimeHoursPerWeek: 37.5,
  proRataBySchedule: true,
  proRataPartialPeriod: true,
  rounding: 'none',
  cycleType: 'company_year',
  yearStartMonth: 1,
  yearStartDay: 1,
  carryOverMode: 'capped',
  carryOverMax: 5,
  carryOverExpiryMonths: '',
  deductPublicHolidays: false,
  publicHolidaysApply: true,
  balancesFrom: '',
};

const n = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const amount = (v, unit) => {
  const x = Math.round(Number(v) * 100) / 100;
  return `${x} ${unit === 'hours' ? (x === 1 ? 'hour' : 'hours') : (x === 1 ? 'day' : 'days')}`;
};

/** One-line summary of a policy, e.g. "25 days a year · carry over up to 5 days · public holidays not deducted" */
function describePolicy(p) {
  if (p.allowanceSource === 'per_employee') {
    return 'Each person\'s allowance, carry forward and time in lieu are set on their profile.';
  }
  const parts = [];
  if (p.entitlementMethod === 'accrual') {
    const rate = n(p.accrualRatePerHour);
    parts.push(p.accrualBasis === 'hours_worked' && rate
      ? `${rate} ${p.unit === 'hours' ? 'hours' : 'days'} per hour worked`
      : `${amount(p.annualEntitlement, p.unit)} a year, accruing ${FREQUENCY_LABEL[p.accrualFrequency] || 'over time'}${p.accrualBasis === 'hours_worked' ? ' with hours worked' : ''}`);
  } else {
    parts.push(`${amount(p.annualEntitlement, p.unit)} a year`);
  }
  parts.push(p.cycleType === 'employment_anniversary'
    ? '12-month cycle from start date'
    : `year starts ${Number(p.yearStartDay)} ${MONTHS[(Number(p.yearStartMonth) || 1) - 1]}`);
  if (p.carryOverMode === 'none') parts.push('no carry over');
  else if (p.carryOverMode === 'unlimited') parts.push('unused leave carries over');
  else parts.push(`carry over up to ${amount(p.carryOverMax ?? 0, p.unit)}`);
  if (n(p.carryOverExpiryMonths)) parts.push(`carried leave expires after ${p.carryOverExpiryMonths} months`);
  parts.push(p.publicHolidaysApply === false
    ? 'no public holidays'
    : 'public holidays not deducted');
  return parts.join(' · ');
}

// ─── Small form helpers ──────────────────────────────────────────────────────
function Field({ label, hint, children, className }) {
  return (
    <div className={cx('min-w-0', className)}>
      <label className={labelClass}>{label}</label>
      {children}
      {hint && <p className="m-0 mt-1 text-xs leading-relaxed text-sand-500">{hint}</p>}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-sand-900">{label}</div>
        {hint && <p className="m-0 mt-0.5 text-xs leading-relaxed text-sand-500">{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} label={undefined} />
    </div>
  );
}

function Group({ title, children }) {
  return (
    <fieldset className="m-0 min-w-0 border-0 border-t border-sand-100 p-0 pt-4">
      <legend className="mb-3 p-0 text-[11px] font-semibold uppercase tracking-wider text-sand-500">{title}</legend>
      <div className="space-y-4">{children}</div>
    </fieldset>
  );
}

// ─── Editor ──────────────────────────────────────────────────────────────────
function PolicyEditor({ initial, templateKey, onClose, onSaved }) {
  const [p, setP] = useState(() => ({
    ...BLANK_POLICY,
    ...Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v !== null && v !== undefined)),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !initial?.id;
  const set = (key) => (value) => setP(prev => ({ ...prev, [key]: value }));
  const setInput = (key) => (e) => setP(prev => ({ ...prev, [key]: e.target.value }));
  const perEmployee = p.allowanceSource === 'per_employee';
  const accrual = p.entitlementMethod === 'accrual';
  const unitWord = p.unit === 'hours' ? 'hours' : 'days';

  async function save() {
    if (!String(p.name).trim()) { setError('Give the policy a name.'); return; }
    setSaving(true);
    setError('');
    const body = {
      ...p,
      name: String(p.name).trim(),
      annualEntitlement: n(p.annualEntitlement) ?? 0,
      accrualRatePerHour: n(p.accrualRatePerHour),
      fullTimeDaysPerWeek: n(p.fullTimeDaysPerWeek),
      fullTimeHoursPerWeek: n(p.fullTimeHoursPerWeek),
      carryOverMax: p.carryOverMode === 'capped' ? (n(p.carryOverMax) ?? 0) : null,
      carryOverExpiryMonths: n(p.carryOverExpiryMonths),
      yearStartMonth: Number(p.yearStartMonth),
      yearStartDay: Number(p.yearStartDay),
      balancesFrom: p.balancesFrom || null,
      ...(isNew && templateKey && { templateKey }),
    };
    delete body.id; delete body.warnings; delete body.assignedUsers; delete body.departments;
    delete body.companyId; delete body.createdAt; delete body.updatedAt; delete body.deletedAt; delete body.isDefault;
    try {
      const res = isNew ? await apiService.createLeavePolicy(body) : await apiService.updateLeavePolicy(initial.id, body);
      const saved = res?.data ?? res;
      toast.success(isNew ? 'Policy created' : 'Policy saved');
      onSaved(saved);
    } catch (e) {
      setError(errorMessage(e, "Couldn't save the policy."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-2xl" labelledBy="policy-editor-title">
      <ModalHeader
        id="policy-editor-title"
        title={isNew ? 'New leave policy' : `Edit ${initial.name}`}
        subtitle="Every setting can be changed to match your employment contracts."
        onClose={onClose}
        leading={<ModalIcon icon={Layers} />}
      />
      <ModalBody>
        <div className="space-y-5 pb-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name">
              <input className={inputClass} value={p.name} onChange={setInput('name')} placeholder="e.g. Part-time staff" autoFocus />
            </Field>
            <Field label="Description (optional)">
              <input className={inputClass} value={p.description ?? ''} onChange={setInput('description')} placeholder="Who it's for" />
            </Field>
          </div>

          <Group title="Allowance">
            <Field label="How allowances are set">
              <Segmented
                size="md"
                className="w-full"
                value={p.allowanceSource}
                onChange={set('allowanceSource')}
                options={[
                  { value: 'policy', label: 'Worked out by this policy' },
                  { value: 'per_employee', label: 'Typed in for each person' },
                ]}
              />
            </Field>

            {!perEmployee && (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Track leave in">
                    <Segmented size="md" className="w-full" value={p.unit} onChange={set('unit')} options={[{ value: 'days', label: 'Days' }, { value: 'hours', label: 'Hours' }]} />
                  </Field>
                  <Field label="Allowance is">
                    <Segmented
                      size="md"
                      className="w-full"
                      value={p.entitlementMethod}
                      onChange={set('entitlementMethod')}
                      options={[{ value: 'fixed', label: 'Given for the year' }, { value: 'accrual', label: 'Built up over time' }]}
                    />
                  </Field>
                </div>

                <Field
                  label={`Full-time allowance per year (${unitWord})`}
                  hint={accrual ? 'What a full-time employee builds up over a whole year.' : 'Part-timers and people who join or leave part-way through are pro-rated below.'}
                >
                  <input type="number" min={0} step={0.5} className={cx(inputClass, 'max-w-[200px]')} value={p.annualEntitlement} onChange={setInput('annualEntitlement')} />
                </Field>

                {accrual && (
                  <div className="grid grid-cols-1 gap-4 rounded-xl bg-sand-50 p-4 sm:grid-cols-2">
                    <Field label="Builds up with">
                      <select className={selectClass} value={p.accrualBasis || 'calendar'} onChange={setInput('accrualBasis')}>
                        <option value="calendar">Time employed</option>
                        <option value="hours_worked">Hours worked (scheduled)</option>
                      </select>
                    </Field>
                    <Field label="Added to the balance">
                      <select className={selectClass} value={p.accrualFrequency || 'daily'} onChange={setInput('accrualFrequency')}>
                        <option value="daily">Continuously (every day)</option>
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </Field>
                    {p.accrualBasis === 'hours_worked' && (
                      <Field label={`Rate per hour worked (${unitWord}, optional)`} hint="e.g. 0.0588 for 1 hour per 17 hours worked. Leave blank to work it out from the yearly allowance.">
                        <input type="number" min={0} max={1} step={0.0001} className={inputClass} value={p.accrualRatePerHour ?? ''} onChange={setInput('accrualRatePerHour')} />
                      </Field>
                    )}
                    <div className="sm:col-span-2">
                      <ToggleRow
                        label="Pause on unpaid leave"
                        hint="Nothing builds up on days of approved unpaid leave."
                        checked={!!p.pauseAccrualOnUnpaidLeave}
                        onChange={set('pauseAccrualOnUnpaidLeave')}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </Group>

          {!perEmployee && (
            <Group title="Full-time and part-time">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Full-time days per week">
                  <input type="number" min={0.5} max={7} step={0.5} className={inputClass} value={p.fullTimeDaysPerWeek} onChange={setInput('fullTimeDaysPerWeek')} />
                </Field>
                <Field label="Full-time hours per week">
                  <input type="number" min={1} max={168} step={0.25} className={inputClass} value={p.fullTimeHoursPerWeek} onChange={setInput('fullTimeHoursPerWeek')} />
                </Field>
              </div>
              <ToggleRow
                label="Pro-rate part-timers"
                hint={`Uses each person's schedule: someone working ${p.unit === 'hours' ? 'half the hours' : '3 of 5 days'} gets ${p.unit === 'hours' ? 'half' : '3/5'} of the allowance.`}
                checked={!!p.proRataBySchedule}
                onChange={set('proRataBySchedule')}
              />
              {!accrual && (
                <>
                  <ToggleRow
                    label="Pro-rate joiners and leavers"
                    hint="Someone who starts or leaves part-way through the year gets a share of the allowance."
                    checked={!!p.proRataPartialPeriod}
                    onChange={set('proRataPartialPeriod')}
                  />
                  <Field label="Round pro-rated allowances">
                    <select className={cx(selectClass, 'max-w-xs')} value={p.rounding} onChange={setInput('rounding')}>
                      <option value="none">Don't round</option>
                      <option value="up_half">Up to the next half</option>
                      <option value="nearest_half">To the nearest half</option>
                      <option value="up_whole">Up to the next whole {p.unit === 'hours' ? 'hour' : 'day'}</option>
                      <option value="nearest_whole">To the nearest whole {p.unit === 'hours' ? 'hour' : 'day'}</option>
                    </select>
                  </Field>
                </>
              )}
            </Group>
          )}

          <Group title="Leave year">
            <Segmented
              size="md"
              className="w-full"
              value={p.cycleType}
              onChange={set('cycleType')}
              options={[
                { value: 'company_year', label: 'Same dates for everyone' },
                { value: 'employment_anniversary', label: '12 months from each start date' },
              ]}
            />
            {p.cycleType === 'company_year' ? (
              <div className="grid grid-cols-2 gap-4 sm:max-w-md">
                <Field label="Starts on (day)">
                  <input type="number" min={1} max={31} className={inputClass} value={p.yearStartDay} onChange={setInput('yearStartDay')} />
                </Field>
                <Field label="Month">
                  <select className={selectClass} value={p.yearStartMonth} onChange={setInput('yearStartMonth')}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </Field>
              </div>
            ) : (
              <p className="m-0 text-xs text-sand-500">Each person's leave cycle starts on the anniversary of their start date (set on their profile). People without a start date use 1 January.</p>
            )}
          </Group>

          {!perEmployee && (
            <Group title="Unused leave">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="At the end of the year">
                  <select className={selectClass} value={p.carryOverMode} onChange={setInput('carryOverMode')}>
                    <option value="capped">Carry over, up to a limit</option>
                    <option value="unlimited">Carry over all of it</option>
                    <option value="none">Lose it</option>
                  </select>
                </Field>
                {p.carryOverMode === 'capped' && (
                  <Field label={`Limit (${unitWord})`}>
                    <input type="number" min={0} step={0.5} className={inputClass} value={p.carryOverMax ?? ''} onChange={setInput('carryOverMax')} />
                  </Field>
                )}
                {p.carryOverMode !== 'none' && (
                  <Field label="Use within (months)" hint="Blank = never expires.">
                    <input type="number" min={1} max={60} className={inputClass} value={p.carryOverExpiryMonths ?? ''} onChange={setInput('carryOverExpiryMonths')} />
                  </Field>
                )}
              </div>
            </Group>
          )}

          <Group title="Public holidays">
            <ToggleRow
              label="People on this policy get public holidays"
              hint="Off for staff who don't get public holidays (e.g. some part-timers): the dates are ordinary working days for them and aren't shown as holidays. You can also switch this off for one person on their Allowance tab."
              checked={p.publicHolidaysApply !== false}
              onChange={set('publicHolidaysApply')}
            />
          </Group>

          {!perEmployee && (
            <Group title="History">
              <Field
                label="Work out balances from"
                hint="Leave before this date isn't counted. Add opening balances on this date for people moving from another system."
              >
                <input type="date" className={cx(inputClass, 'max-w-[220px]')} value={p.balancesFrom ? String(p.balancesFrom).slice(0, 10) : ''} onChange={setInput('balancesFrom')} />
              </Field>
            </Group>
          )}

          {error && <Notice tone="error">{error}</Notice>}
        </div>
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={save}>{isNew ? 'Create policy' : 'Save policy'}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Template picker ─────────────────────────────────────────────────────────
function TemplatePicker({ onClose, onPick }) {
  const [templates, setTemplates] = useState(null);
  const [market, setMarket] = useState(null);

  useEffect(() => {
    apiService.getLeavePolicyTemplates()
      .then(res => { setTemplates(res?.data ?? []); setMarket(res?.meta?.market ?? null); })
      .catch(() => setTemplates([]));
  }, []);

  const groups = useMemo(() => {
    const out = new Map();
    for (const t of templates || []) {
      if (!out.has(t.marketName)) out.set(t.marketName, []);
      out.get(t.marketName).push(t);
    }
    return [...out.entries()];
  }, [templates]);

  return (
    <Modal onClose={onClose} className="max-w-xl" labelledBy="tpl-title">
      <ModalHeader id="tpl-title" title="New leave policy" subtitle="Start from a local template, then adjust anything." onClose={onClose} leading={<ModalIcon icon={Plus} />} />
      <ModalBody>
        {!templates ? <SkeletonRows rows={4} /> : (
          <div className="space-y-5 pb-3">
            {groups.map(([marketName, list]) => (
              <div key={marketName}>
                <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">
                  {marketName}
                  {list[0]?.market === market && <Chip tone="ink">Your country</Chip>}
                </div>
                <div className="space-y-2">
                  {list.map(t => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => onPick(t)}
                      className="block w-full rounded-xl border border-sand-200 bg-[color:var(--surface)] p-3 text-left transition-colors hover:border-sand-300 hover:bg-sand-50"
                    >
                      <span className="block text-sm font-semibold text-sand-900">{t.name}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-sand-600">{t.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onPick(null)}
              className="block w-full rounded-xl border border-dashed border-sand-300 p-3 text-left text-sm font-medium text-sand-700 hover:bg-sand-50"
            >
              Start from scratch
            </button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

// ─── Who's on a policy ───────────────────────────────────────────────────────
function AssignModal({ policy, users, departments, onClose, onSaved }) {
  const [tab, setTab] = useState('people');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set(users.filter(u => u.leavePolicyId === policy.id).map(u => u.id)));
  const [deptSel, setDeptSel] = useState(() => new Set(departments.filter(d => d.leavePolicyId === policy.id).map(d => d.id)));
  const [saving, setSaving] = useState(false);

  const visible = users.filter(u => {
    const q = query.trim().toLowerCase();
    return !q || fullName(u).toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q);
  });

  const toggle = (setFn) => (id) => setFn(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function save() {
    setSaving(true);
    try {
      await apiService.setLeavePolicyMembers(policy.id, [...selected]);
      const changes = departments.filter(d => (d.leavePolicyId === policy.id) !== deptSel.has(d.id));
      for (const d of changes) {
        await apiService.setDepartmentLeavePolicy(d.id, deptSel.has(d.id) ? policy.id : null);
      }
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-lg" labelledBy="assign-title">
      <ModalHeader
        id="assign-title"
        title={`Who's on ${policy.name}`}
        subtitle="A person's own policy wins over their department's; everyone else is on the default."
        onClose={onClose}
        leading={<ModalIcon icon={Users} />}
      />
      <ModalBody>
        <Segmented
          className="mb-3"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'people', label: 'People', count: selected.size },
            { value: 'departments', label: 'Departments', count: deptSel.size },
          ]}
        />
        {tab === 'people' ? (
          <>
            <SearchInput value={query} onChange={setQuery} placeholder="Search people…" className="mb-2" />
            <div className="max-h-80 space-y-1 overflow-y-auto pb-2">
              {visible.map(u => (
                <label key={u.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-sand-50">
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(setSelected)(u.id)} className="h-4 w-4 accent-[#1C1A14]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-sand-900">{fullName(u)}</span>
                    <span className="block truncate text-xs text-sand-500">{u.email}</span>
                  </span>
                </label>
              ))}
              {!visible.length && <p className="m-0 py-4 text-center text-sm text-sand-500">No one matches.</p>}
            </div>
          </>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto pb-2">
            {departments.map(d => (
              <label key={d.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-sand-50">
                <input type="checkbox" checked={deptSel.has(d.id)} onChange={() => toggle(setDeptSel)(d.id)} className="h-4 w-4 accent-[#1C1A14]" />
                <Building2 size={15} className="text-sand-500" />
                <span className="text-sm font-medium text-sand-900">{d.name}</span>
              </label>
            ))}
            {!departments.length && <p className="m-0 py-4 text-center text-sm text-sand-500">No departments yet.</p>}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={save}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Opening balances import ─────────────────────────────────────────────────
function ImportModal({ onClose }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [unit, setUnit] = useState('days');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const rows = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(line => {
    const [email, value] = line.split(/[,;\t]/).map(s => s.trim());
    return { email, amount: Number(value) };
  });
  const bad = rows.filter(r => !r.email || !Number.isFinite(r.amount));

  async function run() {
    setBusy(true);
    try {
      const res = await apiService.importOpeningBalances({ date, unit, rows });
      setResult(res?.data ?? null);
      if (res?.data?.imported) toast.success(`Imported ${res.data.imported} opening balance${res.data.imported === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't import."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-lg" labelledBy="import-title">
      <ModalHeader
        id="import-title"
        title="Import opening balances"
        subtitle="For people moving from another system: their remaining balance on a date. Leave and accrual from that date are added on top."
        onClose={onClose}
        leading={<ModalIcon icon={Upload} />}
      />
      <ModalBody>
        <div className="space-y-4 pb-2">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Balance as at">
              <input type="date" className={inputClass} value={date} onChange={e => setDate(e.target.value)} />
            </Field>
            <Field label="Amounts are in">
              <Segmented size="md" className="w-full" value={unit} onChange={setUnit} options={[{ value: 'days', label: 'Days' }, { value: 'hours', label: 'Hours' }]} />
            </Field>
          </div>
          <Field label="One person per line: email, balance" hint="Paste from a spreadsheet — comma, semicolon or tab separated.">
            <textarea className={cx(textareaClass, 'font-mono text-[13px]')} rows={7} value={text} onChange={e => { setText(e.target.value); setResult(null); }} placeholder={'jane@company.com, 12.5\nsam@company.com, 4'} />
          </Field>
          {bad.length > 0 && <Notice tone="warn">{bad.length} line{bad.length === 1 ? '' : 's'} need an email and a number.</Notice>}
          {result && (
            <Notice tone={result.failed ? 'warn' : 'info'}>
              Imported {result.imported}.{' '}
              {result.failed > 0 && (
                <>Not imported: {result.results.filter(r => !r.ok).map(r => `line ${r.row} (${r.error})`).join(', ')}.</>
              )}
            </Notice>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        <Button variant="primary" loading={busy} disabled={!rows.length || bad.length > 0} onClick={run}>Import {rows.length || ''}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Main section ────────────────────────────────────────────────────────────
export default function LeavePoliciesSection() {
  const confirm = useConfirm();
  const [policies, setPolicies] = useState(null);
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(false);
  const [editing, setEditing] = useState(null);       // { initial, templateKey }
  const [assigning, setAssigning] = useState(null);   // policy
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [polRes, usersRes, deptRes] = await Promise.all([
        apiService.getLeavePolicies(),
        apiService.getCompanyUsers({ status: 'active' }).catch(() => null),
        apiService.getDepartments().catch(() => null),
      ]);
      setPolicies(polRes?.data ?? []);
      setMeta(polRes?.meta ?? null);
      setUsers(unwrapList(usersRes, 'users'));
      setDepartments(unwrapList(deptRes, 'departments'));
    } catch (e) {
      setError(errorMessage(e, "Couldn't load leave policies."));
      setPolicies(prev => prev ?? []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defaultPolicy = (policies || []).find(p => p.isDefault);
  const onDefaultCount = useMemo(() => {
    if (!policies || !defaultPolicy) return 0;
    const deptPolicy = new Map(departments.map(d => [d.id, d.leavePolicyId]));
    return users.filter(u => {
      if (u.leavePolicyId) return false;
      const deptId = u.departments?.[0]?.departmentId;
      return !deptId || !deptPolicy.get(deptId);
    }).length;
  }, [policies, defaultPolicy, users, departments]);

  async function makeDefault(policy) {
    try {
      await apiService.setDefaultLeavePolicy(policy.id);
      toast.success(`${policy.name} is now the default`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't change the default."));
    }
  }

  async function remove(policy) {
    const ok = await confirm({
      title: `Delete ${policy.name}?`,
      message: 'People and departments on it move to the default policy. Their leave history is kept.',
      confirmLabel: 'Delete policy',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteLeavePolicy(policy.id);
      toast.success('Policy deleted');
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't delete the policy."));
    }
  }

  return (
    <div>
      <SectionHeader
        title="Leave policies"
        description="How annual leave works for each group of people. Templates give sensible local defaults; you can change every value to match your contracts."
        action={(
          <>
            <Button size="sm" icon={Upload} onClick={() => setImporting(true)}>Opening balances</Button>
            <Button size="sm" variant="primary" icon={Plus} onClick={() => setPicker(true)}>New policy</Button>
          </>
        )}
      />

      {meta?.statutoryNote && <Notice tone="info" className="mb-5">{meta.statutoryNote}</Notice>}
      {error && <Notice tone="error" className="mb-5">{error}</Notice>}

      {!policies ? <SkeletonRows rows={3} height="h-24" /> : policies.length === 0 ? (
        <EmptyState icon={Layers} title="No leave policies yet" message="Create one from a template to get started.">
          <Button variant="primary" icon={Plus} onClick={() => setPicker(true)}>New policy</Button>
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {policies.map(policy => (
            <div key={policy.id} className={cx('rounded-2xl border p-4 sm:p-5', policy.isActive === false ? 'border-dashed border-sand-200 opacity-70' : 'border-sand-200')}>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ink-50 text-ink"><Layers size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="m-0 text-[15px] font-semibold text-sand-900">{policy.name}</h3>
                    {policy.isDefault && <Chip tone="ink"><Star size={10} /> Default</Chip>}
                    {policy.allowanceSource !== 'per_employee' && (
                      <Chip tone="sand">{policy.entitlementMethod === 'accrual' ? 'Accruing' : 'Fixed'} · {policy.unit}</Chip>
                    )}
                    {policy.market && <Chip tone="sand">{policy.market === 'GB' ? 'UK' : policy.market}</Chip>}
                  </div>
                  <p className="m-0 mt-1 text-[13px] leading-relaxed text-sand-600">{describePolicy(policy)}</p>
                  <p className="m-0 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-sand-500">
                    <span className="inline-flex items-center gap-1"><Users size={12} />
                      {policy.isDefault
                        ? `${onDefaultCount} ${onDefaultCount === 1 ? 'person' : 'people'} by default${policy.assignedUsers ? ` + ${policy.assignedUsers} assigned` : ''}`
                        : `${policy.assignedUsers || 0} ${policy.assignedUsers === 1 ? 'person' : 'people'}`}
                    </span>
                    {policy.departments?.length > 0 && (
                      <span className="inline-flex items-center gap-1"><Building2 size={12} /> {policy.departments.map(d => d.name).join(', ')}</span>
                    )}
                  </p>
                  {policy.warnings?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {policy.warnings.map((w, i) => <Notice key={i} tone="warn">{w}</Notice>)}
                    </div>
                  )}
                </div>
                <Menu
                  trigger={({ toggle }) => (
                    <IconButton label="Policy actions" onClick={toggle}><MoreHorizontal size={17} /></IconButton>
                  )}
                  items={[
                    { label: 'Edit', icon: Edit2, onClick: () => setEditing({ initial: policy }) },
                    { label: 'Choose who\'s on it', icon: Users, hint: 'People and departments', onClick: () => setAssigning(policy) },
                    !policy.isDefault && { label: 'Make default', icon: Check, hint: 'For everyone without another policy', onClick: () => makeDefault(policy) },
                    !policy.isDefault && { label: 'Delete', icon: Trash2, tone: 'danger', onClick: () => remove(policy) },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {picker && (
        <TemplatePicker
          onClose={() => setPicker(false)}
          onPick={(tpl) => {
            setPicker(false);
            setEditing(tpl
              ? { initial: { ...tpl.policy, name: tpl.name, description: tpl.description }, templateKey: tpl.key }
              : { initial: null });
          }}
        />
      )}
      {editing && (
        <PolicyEditor
          initial={editing.initial}
          templateKey={editing.templateKey}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {assigning && (
        <AssignModal
          policy={assigning}
          users={users}
          departments={departments}
          onClose={() => setAssigning(null)}
          onSaved={() => { setAssigning(null); load(); }}
        />
      )}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
    </div>
  );
}